import {
  Component,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  Inject,
  PLATFORM_ID,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterLink } from "@angular/router";
import { isPlatformBrowser } from "@angular/common";

const CARD_GAP = 0.3;
const SCROLL_INITIAL = 160;
const SCROLL_STEP = 55;
const SCROLL_LOADER = 40; // extra scroll up so loader is centered
const SCROLL_FINAL = -120; // final position for cards

interface MessageStep {
  bubble: string;
  target: string | null;
  text: string | null;
  speed: number;
}

/** Demo job cards: NYC, Remote, min $130k */
export interface DemoJob {
  logo: string;
  company: string;
  title: string;
  matchPct: number;
  salary: string;
  tags: string[];
  description: string;
  /** Optional extra line (e.g. "Before you apply") - only card 2 has it */
  tip?: string;
}

const DEMO_JOBS: DemoJob[] = [
  {
    logo: "assets/images/homepage/google.png",
    company: "Google",
    title: "UX Designer",
    matchPct: 85,
    salary: "$130k – $175k",
    tags: ["Search", "Mobile"],
    description:
      "Shape experiences used by billions. Design across Search, Android, and ecosystem products. NYC or remote.",
  },
  {
    logo: "assets/images/homepage/cursor.png",
    company: "Cursor",
    title: "Senior Product Designer",
    matchPct: 86,
    salary: "$145k – $185k",
    tags: ["AI", "Developer tools"],
    description:
      "Design the future of AI-powered coding. Create intuitive flows for developers building with Cursor.",
  },
  {
    logo: "assets/images/homepage/github-copilot.png",
    company: "GitHub Copilot",
    title: "Product Designer",
    matchPct: 88,
    salary: "$140k – $175k",
    tags: ["AI", "Code"],
    description:
      "Design how developers interact with AI pair programming. Shape Copilot experiences across GitHub and IDEs.",
  },
  {
    logo: "assets/images/homepage/linear.png",
    company: "Linear",
    title: "Product Designer",
    matchPct: 89,
    salary: "$150k – $195k",
    tags: ["Design tools", "Speed"],
    description:
      "Design the tool that ships. Work on Linear’s interface and help teams ship software faster.",
  },
  {
    logo: "assets/images/homepage/hugging-face.png",
    company: "Hugging Face",
    title: "Staff Product Designer",
    matchPct: 91,
    salary: "$155k – $200k",
    tags: ["AI / ML", "Open source"],
    description:
      "Design the hub for ML models and datasets. Make AI accessible for researchers and builders worldwide.",
    tip: "Adapt your resume for better chances",
  },
  {
    logo: "assets/images/homepage/openai.png",
    company: "OpenAI",
    title: "Senior Product Designer",
    matchPct: 94,
    salary: "$165k – $215k",
    tags: ["AI", "ChatGPT"],
    description:
      "Design the user experience for ChatGPT and OpenAI products. NYC or remote. Collaborate with research and product.",
  },
];

@Component({
  selector: "app-job-matcher-animation",
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: "./job-matcher-animation.component.html",
  styleUrls: ["./job-matcher-animation.component.scss"],
})
export class JobMatcherAnimationComponent implements AfterViewInit, OnDestroy {
  @ViewChild("scene") sceneRef!: ElementRef<HTMLElement>;

  /** Stack order: [0] = back (Card 6), [5] = front (Card 1). */
  readonly demoJobs = DEMO_JOBS;

  private readonly MESSAGES: MessageStep[] = [
    {
      bubble: "b1",
      target: "t1",
      text: "Find me Senior Product Design roles in NYC 🇺🇸",
      speed: 30,
    },
    {
      bubble: "b2",
      target: "t2",
      text: "For AI related companies, remote only",
      speed: 35,
    },
    {
      bubble: "b3",
      target: "t3",
      text: "Min salary of $130k 💰",
      speed: 38,
    },
    { bubble: "b4", target: null, text: null, speed: 0 },
  ];

  private loopAborted = false;
  private isBrowser = false;

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngAfterViewInit(): void {
    if (this.isBrowser && this.sceneRef?.nativeElement) {
      this.startLoop();
    }
  }

  ngOnDestroy(): void {
    this.loopAborted = true;
  }

  private get root(): HTMLElement | null {
    return this.sceneRef?.nativeElement ?? null;
  }

  private q<T extends Element = Element>(id: string): T | null {
    return this.root?.querySelector<T>(`#${id}`) ?? null;
  }

  private wait(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms));
  }

  private typeText(el: HTMLElement, text: string, speed: number): Promise<void> {
    return new Promise((resolve) => {
      const bubble = el.closest(".bubble-base") as HTMLElement;
      if (!bubble) {
        resolve();
        return;
      }
      bubble.style.maxWidth = "none";
      el.textContent = text;
      const finalWidth = bubble.offsetWidth;
      el.textContent = "";

      bubble.style.width = `${finalWidth}px`;

      let i = 0;
      const iv = setInterval(() => {
        if (this.loopAborted) {
          clearInterval(iv);
          resolve();
          return;
        }
        el.textContent = text.slice(0, ++i);
        if (i >= text.length) {
          clearInterval(iv);
          resolve();
        }
      }, speed);
    });
  }

  private async runSequence(): Promise<void> {
    const el = this.root;
    if (!el) return;
    const get = (id: string) => el.querySelector<HTMLElement>(`#${id}`);

    el.querySelectorAll(".bubble-base").forEach((b) => {
      b.classList.remove("visible");
      const style = (b as HTMLElement).style;
      style.width = "";
      style.maxWidth = "";
    });
    el.querySelectorAll(".typing-target").forEach((t) => {
      t.textContent = "";
      t.classList.remove("done");
    });
    get("sceneContent")?.style.setProperty("transform", `translateY(${SCROLL_INITIAL}px)`);
    get("loader")?.classList.remove("show", "hide");
    get("progressRing")?.classList.remove("animate");
    get("lt1")?.classList.remove("active");
    get("lt2")?.classList.remove("active");
    get("successMsg")?.classList.remove("show");
    get("stack")?.classList.remove("show");

    await this.wait(100);

    const sceneContent = get("sceneContent");
    let scrollY = SCROLL_INITIAL;

    for (let idx = 0; idx < this.MESSAGES.length; idx++) {
      if (this.loopAborted) return;
      const msg = this.MESSAGES[idx];
      get(msg.bubble)?.classList.add("visible");

      if (msg.target && msg.text) {
        const target = get(msg.target);
        if (target) {
          await this.typeText(target, msg.text, msg.speed);
          target.classList.add("done");
        }
        if (idx > 0) {
          scrollY -= SCROLL_STEP;
          sceneContent?.style.setProperty("transform", `translateY(${scrollY}px)`);
        }
        await this.wait(200);
      } else {
        scrollY -= SCROLL_STEP;
        sceneContent?.style.setProperty("transform", `translateY(${scrollY}px)`);
        await this.wait(300);
      }
    }

    await this.wait(400);

    const loader = get("loader");
    const ring = get("progressRing");
    loader?.classList.add("show");
    scrollY -= SCROLL_LOADER;
    sceneContent?.style.setProperty("transform", `translateY(${scrollY}px)`);
    await this.wait(100);
    ring?.classList.add("animate");
    get("lt1")?.classList.add("active");
    await this.wait(1600);
    get("lt1")?.classList.remove("active");
    await this.wait(100);
    get("lt2")?.classList.add("active");
    await this.wait(1600);
    loader?.classList.add("hide");
    await this.wait(500);

    get("successMsg")?.classList.add("show");
    sceneContent?.style.setProperty("transform", `translateY(${SCROLL_FINAL}px)`);
    await this.wait(600);

    const stackEl = get("stack");
    if (!stackEl) return;

    const base = 1.7;
    const step = 1.8;
    const t1btn = base;
    const t1swp = t1btn + CARD_GAP;
    const t2btn = t1swp + step;
    const t2swp = t2btn + CARD_GAP;
    const t3btn = t2swp + step;
    const t3swp = t3btn + CARD_GAP;
    const t4btn = t3swp + step;
    const t4swp = t4btn + CARD_GAP;
    const t5btn = t4swp + step;
    const t5swp = t5btn + CARD_GAP;

    stackEl.style.setProperty("--c1-btn", `${t1btn}s`);
    stackEl.style.setProperty("--c1-swipe", `${t1swp}s`);
    stackEl.style.setProperty("--c2-btn", `${t2btn}s`);
    stackEl.style.setProperty("--c2-swipe", `${t2swp}s`);
    stackEl.style.setProperty("--c3-btn", `${t3btn}s`);
    stackEl.style.setProperty("--c3-swipe", `${t3swp}s`);
    stackEl.style.setProperty("--c4-btn", `${t4btn}s`);
    stackEl.style.setProperty("--c4-swipe", `${t4swp}s`);
    stackEl.style.setProperty("--c5-btn", `${t5btn}s`);
    stackEl.style.setProperty("--c5-swipe", `${t5swp}s`);
    stackEl.classList.add("show");

    const totalCardTime = (t5swp + 2) * 1000;
    await this.wait(totalCardTime);
  }

  private async startLoop(): Promise<void> {
    const stackEl = this.q<HTMLElement>("stack");
    if (!stackEl) return;

    while (!this.loopAborted) {
      const stackHTML = stackEl.innerHTML;
      stackEl.innerHTML = "";
      void stackEl.offsetWidth;
      stackEl.innerHTML = stackHTML;

      await this.runSequence();
      if (this.loopAborted) break;
      await this.wait(800);
    }
  }
}
